import type { FC } from "react";
import { useMemo } from "react";
import styled from "@emotion/styled";
import { ArrowRight, ExternalLink } from "lucide-react";
import { colors, radius, spacing } from "../design-tokens";
import { BodySmall } from "@/components/primitives/typography";
import {
  isInternalUrl,
  isPdfUrl,
  stripProtocolAndWww,
} from "@/utils/url-helpers";
import type { SourceUrlUIPart } from "ai";

const StyledSourcesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-auto-rows: min-content;
  width: 100%;
  box-sizing: border-box;
`;

const SourceItem = styled.a`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${spacing.md}px;
  padding: ${spacing.lg}px 0;
  min-width: 0;
  cursor: pointer;
  text-decoration: none;
  border-bottom: 1px solid ${colors.border};

  &:first-of-type {
    padding-top: 0;
  }

  &:last-of-type {
    border-bottom: none;
  }

  &:hover {
    opacity: 0.8;
  }
`;

const SourceTextContent = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${spacing.xs}px;
  min-width: 0;
`;

const SourceTitle = styled(BodySmall)`
  display: block;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SourceUrlContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${spacing.sm}px;
  justify-content: flex-start;
  align-items: center;
  max-width: 250px;
`;

const SourceUrl = styled.span`
  color: ${colors.textTertiary};
  font-size: 12px;
  margin: 0;
  overflow: hidden;
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-overflow: ellipsis;
  text-underline-offset: 2px;
  white-space: nowrap;
`;

const Badge = styled.span`
  width: fit-content;
  margin: 0;
  padding: 2px 4px;
  border-radius: 4px;
  color: ${colors.textTertiary};
  background-color: ${colors.background};
  font-size: 12px;
`;

const IconWrapper = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: ${spacing.xs}px;
  box-sizing: border-box;
  border-radius: ${radius.sm}px;
  color: ${colors.iconAccent};

  svg {
    width: 100%;
    height: 100%;
  }
`;

type SourcesGridProps = {
  sources: SourceUrlUIPart[];
};

export const SourcesGrid: FC<SourcesGridProps> = ({ sources }) => {
  const processedSources = useMemo(
    () =>
      sources.map(source => ({
        ...source,
        isPdf: isPdfUrl(source.url),
        isInternal: isInternalUrl(source.url),
      })),
    [sources],
  );

  return (
    <StyledSourcesGrid>
      {processedSources.map(source => (
        <SourceItem
          key={source.sourceId}
          href={source.url}
          target="_blank"
          rel="noopener noreferrer"
        >
          <SourceTextContent>
            <SourceTitle>{source.title}</SourceTitle>
            <SourceUrlContainer>
              <SourceUrl>{stripProtocolAndWww(source.url)}</SourceUrl>
            </SourceUrlContainer>
          </SourceTextContent>
          {source.isPdf && <Badge>PDF</Badge>}
          <IconWrapper>
            {source.isInternal ? <ArrowRight /> : <ExternalLink />}
          </IconWrapper>
        </SourceItem>
      ))}
    </StyledSourcesGrid>
  );
};
